import React, {useState, useEffect, useContext} from 'react';
import {Form, Radio, DatePicker} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import GenericDialog from '@common/GenericDialog';
import dayjs from 'dayjs';
import {EDIT_ASSIGN_OBJECT} from '../teacher/enums';
import RecipientSelect, {RECIPIENT_OBJ, RECIPIENT_GROUP_OBJ} from "@common/RecipientSelect";
import axios from "@common/axios";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";

const EditTestAssignmentDialog = props => {
    const {open, onClose, setReload, testAssignment} = props;
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const [editTestAssignmentForm] = Form.useForm();
    const isDiagnosticTest = testAssignment?.diagnosticTest;
    const isInProgress = testAssignment?.inProgress;
    const assignedTestType = testAssignment?.testType;
    const [testType, setTestType] = useState(EDIT_ASSIGN_OBJECT.TEST_TYPE[assignedTestType]);
    const isShowResultsOnExpiry = testAssignment?.showResultsOnExpiry;
    const [startDate, setStartDate] = useState(testAssignment?.startDate);
    const assignedStartDate = dayjs(testAssignment?.startDate);
    const assignedEndDate = dayjs(testAssignment?.endDate);
    const [recipientObj, setRecipientObj] = useState();
    const [classes, setClasses] = useState([]);

    const defaultRecipientSelectObj = {isInProgress: isInProgress, assignedToStudents: testAssignment?.assignedToStudents, studentIds: testAssignment?.studentIds, classIds: testAssignment?.classIds, recipientType: testAssignment?.classIds?.length > 1 ? RECIPIENT_OBJ.MULTIPLE_CLASSES : RECIPIENT_OBJ.SINGLE_CLASS};

    const dialogWidth = isDiagnosticTest ? 600 : 550;
    const formLabelCol = isDiagnosticTest ? 9 : 7;
    const formWrapperCol = isDiagnosticTest ? 15 : 17;

    useEffect(() => {
        if (open) {
            axios.get('/web/ui/myClasses/viewMyClasses')
                .then(response => {
                    setClasses(response.data.filter(clazz => clazz.totalStudents > 0));
                });
            editTestAssignmentForm.setFieldsValue({
                testType: assignedTestType,
                note: testAssignment?.note,
                showResultsOnExpiry: isShowResultsOnExpiry,
                startDate: assignedStartDate,
                endDate: assignedEndDate,
            });
        }
    }, [open]);

    const onTestTypeChange = ({target: {value}}) => {
        setTestType(EDIT_ASSIGN_OBJECT.TEST_TYPE[value]);
    }

    const updateTestAssignment = async () => {
        editTestAssignmentForm.validateFields()
            .then(values => {
                const params = {...values};

                params.testAssignmentId = testAssignment?.testAssignmentId;

                params.startDate = params.startDate.format(user.getDateTimeFormatString());
                params.endDate = params.endDate.format(user.getDateTimeFormatString());

                if (RECIPIENT_GROUP_OBJ.STUDENTS.val !== recipientObj?.recipientGroup?.val) {
                    delete params.studentIds;
                }

                delete params.recipient;

                axios.post('/web/ui/test/teacher/updateTestAssignment', params, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(response => {
                        if (response.data.success) {
                            displayToastMessage('Test assignment updated assigned successfully', null, 'success', undefined, 10000);

                            editTestAssignmentForm.resetFields();
                            setReload(new Date().getTime());
                            if (typeof onClose === 'function') onClose();
                        } else {
                            displayToastMessage('Error updating test assignment test', response.data.message, 'error', undefined, 10000);
                        }
                    }).catch(error => {
                        console.error(error);
                    });
            });
    }

    return (
        <GenericDialog
            title={'Edit test assignment'}
            open={open}
            contentStyle={{width: dialogWidth}}
            onClose={onClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'},
                },
                {
                    name: 'Update',
                    callback: updateTestAssignment,
                }
            ]}
        >
            <Form
                className={'testFamilyForm'}
                form={editTestAssignmentForm}
                labelCol={{span: formLabelCol}}
                wrapperCol={{span: formWrapperCol}}
            >
{isInProgress &&
                    <div className={'ch-text-emphasis'}>Once an {brandConfig.testName} is in progress, you may only adjust the end date and message to students.</div>
                }
                <Form.Item
                    label={'Choose format'}
                    name={'testType'}
                    extra={testType.extraTxt}>
                    {isInProgress &&
                        <div className={'ch-text-emphasis'}>
                            {testType[isDiagnosticTest ? 'diagnosticName' : 'name']}
                        </div>
                    }
                    {!isInProgress &&
                        <Radio.Group
                            optionType={'button'}
                            onChange={onTestTypeChange}
                            options={[
                                {
                                    label: EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT[isDiagnosticTest ? 'diagnosticName' : 'name'],
                                    value: EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT.val,
                                },
                                {
                                    label: EDIT_ASSIGN_OBJECT.TEST_TYPE.REVISION[isDiagnosticTest ? 'diagnosticName' : 'name'],
                                    value: EDIT_ASSIGN_OBJECT.TEST_TYPE.REVISION.val,
                                },
                            ]}
                        />
                    }
                </Form.Item>
                <Form.Item
                    label={'Start date'}
                    name={'startDate'}
                    extra={EDIT_ASSIGN_OBJECT.START_DATE_TOOLTIP}
                    rules={[
                        {
                            required: true,
                            message: 'Please select a start date.',
                        },
                        {
                            validator: (_, value) => {
                                if (assignedStartDate && value && value < assignedStartDate) {
                                    return Promise.reject(
                                        `Start date must not be before (${assignedStartDate.format(
                                            user.getDateFormatString() + ', HH:mm',
                                        )}).`,
                                    );
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        disabled={isInProgress}
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateTimeFormatString()}
                        placement="topLeft"
                        onChange={setStartDate}
                        style={{width: '100%'}}
                    />
                </Form.Item>
                <Form.Item
                    label={'End date'}
                    name={'endDate'}
                    extra={EDIT_ASSIGN_OBJECT.END_DATE_TOOLTIP}
                    rules={[
                        {
                            required: true,
                            message: 'Please select an end date.',
                        },
                        {
                            validator: (_, value) => {
                                const currentDate = dayjs(new Date().getTime()).add(10, 'minute').toDate();
                                if (value && startDate && value < startDate) {
                                    return Promise.reject('End date must be after start date.');
                                }
                                if (value && value < currentDate) {
                                    return Promise.reject('End date must be in more than 10 minutes from now.');
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateTimeFormatString()}
                        placement="topLeft"
                        style={{width: '100%'}}
                    />
                </Form.Item>
                {isDiagnosticTest &&
                    <Form.Item label={'Allow students to view results'} name={'showResultsOnExpiry'}
                               extra={testType.val === EDIT_ASSIGN_OBJECT.TEST_TYPE.ASSESSMENT.val ? EDIT_ASSIGN_OBJECT.SHOW_RESULTS_ONE_SITTING_TOOLTIP : EDIT_ASSIGN_OBJECT.SHOW_RESULTS_MULTIPLE_SITTING_TOOLTIP}>
                        {isInProgress &&
                            <div className={'ch-text-emphasis'}>{isShowResultsOnExpiry ? 'Yes' : 'No'}</div>
                        }
                        {!isInProgress &&
                            <Radio.Group
                                optionType="button"
                                options={[
                                    {label: 'Yes', value: true},
                                    {label: 'No', value: false},
                                ]}
                            />
                        }
                    </Form.Item>
                }
                <Form.Item label={'Message for students'} name={'note'}>
                    <TextArea rows={4}/>
                </Form.Item>
                <RecipientSelect form={editTestAssignmentForm} classes={classes} setRecipientObj={setRecipientObj} defaultRecipientVals={defaultRecipientSelectObj} />
            </Form>
        </GenericDialog>
    );
};

export default EditTestAssignmentDialog;
